/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { db, fetchAdminUsersWithFallback, callFunctionWithFallback } from '../firebase';
import { collection, getDocs, query, orderBy, where, doc, setDoc, writeBatch } from 'firebase/firestore';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    Label, LabelList, AreaChart, Area,
} from 'recharts';
import { downloadFile } from '../utils/downloadUtils';
import { AdminAssistantModal } from './AdminAssistantModal';
import { AdminContentManagement } from './AdminContentManagement';
import {
    TrendingUp, TrendingDown, Clock, ShieldAlert, Sparkles,
    Search, Shield, Mail, Trash2, BarChart2, Activity, Award, AlertCircle,
    UserCog, Bot, Users, FileText, Lightbulb
} from 'lucide-react';

/* ─── Types ─────────────────────────────────────────────── */
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    greeting_and_opening: "Setting the right tone from the start. Focus on professional greetings and setting clear expectations.",
    active_listening_and_understanding: "Demonstrating that you hear and process user needs. Focus on clarifying questions and acknowledging key points.",
    empathy_and_tone: "The human side of service. Focus on validating emotions and maintaining a supportive, professional voice.",
    problem_solving_resolution: "Efficiency and effectiveness. Focus on finding actual solutions and minimizing friction for the customer.",
    communication_clarity: "Being understood. Focus on avoiding jargon, using concise language, and structured explanations.",
    compliance_and_accuracy: "Safety first. Focus on following protocols and providing 100% accurate information or data.",
    closing: "Leaving a lasting impression. Focus on summarizing actions and confirming that all needs were met."
};

interface AdminUser {
    uid: string;
    email: string;
    fullName: string;
    team: string;
    lastSignInTime: string;
    creationTime: string;
    isAdmin?: boolean;
    allocatedMinutes?: number | 'unlimited';
    usedMinutes?: Record<string, number>;
    conversationGuideDisabled?: boolean;
}

type Timeframe = 'daily' | 'weekly' | 'monthly';

/* ─── Pure helpers (module-level, no re-creation on render) ── */
const parseScore = (session: any): number | null => {
    const v = session?.feedback?.overall_score;
    if (typeof v === 'number' && !isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = parseFloat(v);
        return isNaN(n) ? null : n;
    }
    return null;
};

const toLocalDateString = (date: Date) => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const parseDurationSecs = (session: any): number => {
    if (!session?.duration) return 0;
    const val = session.duration;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        // Remove prefixes like "Total Time: " and keep only digits and colons
        const clean = val.replace(/[^\d:]/g, ' ').trim();
        const m = clean.match(/(\d+):(\d+)(?::(\d+))?/);
        if (!m) return 0;
        
        // If m[3] exists, we have H:M:S
        if (m[3]) {
            const h = parseInt(m[1], 10);
            const mn = parseInt(m[2], 10);
            const sc = parseInt(m[3], 10);
            return (h * 3600) + (mn * 60) + sc;
        } else {
            // Otherwise we have M:S
            const mn = parseInt(m[1], 10);
            const sc = parseInt(m[2], 10);
            return (mn * 60) + sc;
        }
    }
    return 0;
};

const formatDuration = (secs: number): string => {
    const mins = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

// Caps: show exactly the last N buckets in the trend chart ending at endDate
const CAPS: Record<Timeframe, number> = { daily: 7, weekly: 4, monthly: 3 };

const CHART_COLORS = ['#A02B93', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

/* ─── Tiny shared components ─────────────────────────────── */
const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0.65rem', padding: '0.6rem 0.9rem', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: '0.82rem' }}>
            {label && <p style={{ color: '#6B7280', fontWeight: 600, marginBottom: '0.3rem' }}>{label}</p>}
            {payload.map((e: any, i: number) => (
                <p key={i} style={{ color: e.color || '#374151', fontWeight: 700 }}>{e.name}: {e.value}{e.unit || ''}</p>
            ))}
        </div>
    );
};

const KpiCard = ({ label, value, icon: Icon, color = 'var(--primary)' }: { label: string; value: string | number; icon: any; color?: string }) => (
    <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', position: 'relative', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: `${color}14`, borderRadius: '0.55rem', padding: '0.45rem', display: 'flex' }}>
            <Icon size={16} color={color} />
        </div>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
        <span style={{ fontSize: '1.75rem', fontWeight: 800, color: '#1F2937', fontFamily: 'Outfit, sans-serif', lineHeight: 1.1 }}>{value}</span>
    </div>
);

const PanelCard = ({ title, icon: Icon, color = 'var(--primary)', children, style }: { title: string; icon: any; color?: string; children: React.ReactNode; style?: React.CSSProperties }) => (
    <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', ...style }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '1.1rem' }}>
            <div style={{ background: `${color}12`, borderRadius: '0.45rem', padding: '0.35rem', display: 'flex' }}>
                <Icon size={14} color={color} />
            </div>
            <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1F2937', fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
        </div>
        {children}
    </div>
);

const EmptyChart = ({ h = 200 }: { h?: number }) => (
    <div style={{ height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D1D5DB', fontSize: '0.85rem' }}>
        No data for current filters
    </div>
);

/* ════════════════════════════════════════════════════════════ */
export const AdminScreen: React.FC = () => {
    const getLast30Days = () => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return toLocalDateString(d);
    };
    const getToday = () => toLocalDateString(new Date());

    /* ── State ──────────────────────────────────────────── */
    const [activeTab, setActiveTab]   = useState<'users' | 'analytics' | 'content'>('analytics');
    const [timeframe,  setTimeframe]  = useState<Timeframe>('weekly');

    const [users,      setUsers]      = useState<AdminUser[]>([]);
    const [aggregates, setAggregates] = useState<any[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [error,    setError]    = useState<string | null>(null);
    const [retryTrigger, setRetryTrigger] = useState(0);
    const [editingAllocationUserId, setEditingAllocationUserId] = useState<string | null>(null);
    const [editAllocationValue, setEditAllocationValue] = useState<string>('');

    const [searchTerm, setSearchTerm] = useState('');
    const [userMgmtTeamFilter, setUserMgmtTeamFilter] = useState<string>('All');
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
    const [teamFilters, setTeamFilters] = useState<string[]>([]);
    const [teamSearch, setTeamSearch] = useState('');
    const [teamSortAsc, setTeamSortAsc] = useState(true);
    const [teamMenuOpen, setTeamMenuOpen] = useState(false);
    const [userFilter, setUserFilter] = useState('All');
    const [userSearch, setUserSearch] = useState('');
    const [userSortAsc, setUserSortAsc] = useState(true);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [startDate,  setStartDate]  = useState(getLast30Days());
    const [endDate,    setEndDate]    = useState(getToday());
    const [showAssistant, setShowAssistant] = useState(false);

    const [channelFilters, setChannelFilters] = useState<string[]>([]);
    const [channelSearch, setChannelSearch] = useState('');
    const [channelSortAsc, setChannelSortAsc] = useState(true);
    const [channelMenuOpen, setChannelMenuOpen] = useState(false);



    // No longer auto-resetting dates on timeframe change to allow user-determined periods.

    /* ── Data fetch ─────────────────────────────────────── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [userList, fsUsersSnap] = await Promise.all([
                    fetchAdminUsersWithFallback(),
                    getDocs(collection(db, 'users')).catch(() => null)
                ]);
                const fsOverlay = new Map<string, any>();
                if (fsUsersSnap) {
                    fsUsersSnap.forEach(d => fsOverlay.set(d.id, d.data()));
                }
                const mergedUsers = userList.map(u => {
                    const ov = fsOverlay.get(u.uid);
                    return {
                        ...u,
                        team: (ov?.teamName || ov?.team || u.team || '').trim(),
                        conversationGuideDisabled: typeof ov?.conversationGuideDisabled === 'boolean'
                            ? ov.conversationGuideDisabled
                            : Boolean((u as any).conversationGuideDisabled)
                    };
                });
                if (!cancelled) setUsers(mergedUsers);
            } catch (e: any) {
                console.warn('Failed to load users list:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [retryTrigger]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const endObj = new Date((endDate || getToday()) + 'T00:00:00');
                const monthlyStart = new Date(endObj.getFullYear(), endObj.getMonth() - 3, 1);
                const weeklyStart = new Date(endObj);
                weeklyStart.setDate(endObj.getDate() - 28);
                const dailyStart = new Date(endObj);
                dailyStart.setDate(endObj.getDate() - 7);
                const userStart = new Date((startDate || getLast30Days()) + 'T00:00:00');
                
                const fetchStart = new Date(Math.min(
                    monthlyStart.getTime(),
                    weeklyStart.getTime(),
                    dailyStart.getTime(),
                    userStart.getTime()
                ));

                // Determine which aggregates to fetch based on filters
                let type = 'global';
                let filterValues: string[] = [];
                if (userFilter !== 'All') {
                    type = 'user';
                    filterValues = [userFilter];
                } else if (teamFilters.length > 0) {
                    type = 'team';
                    filterValues = teamFilters;
                }

                const conditions = [
                    where('type', '==', type),
                    where('date', '>=', toLocalDateString(fetchStart)),
                    where('date', '<=', endDate || toLocalDateString(new Date())),
                ];

                if (type !== 'global') {
                    if (filterValues.length === 1) {
                        conditions.push(where('filterValue', '==', filterValues[0]));
                    } else if (filterValues.length > 1) {
                        // Firebase "in" query limit is 30
                        conditions.push(where('filterValue', 'in', filterValues.slice(0, 30)));
                    }
                }

                const aggQ = query(
                    collection(db, 'dailyAggregates'),
                    ...conditions,
                    orderBy('date', 'desc')
                );
                const aggSnap = await getDocs(aggQ);
                if (cancelled) return;

                const merged: Record<string, any> = {};
                aggSnap.forEach(d => {
                    const data = d.data();
                    const date = data.date;
                    if (!merged[date]) {
                        merged[date] = { 
                            date,
                            sessionsCount: 0,
                            scoredSessionsCount: 0,
                            totalScore: 0,
                            totalDurationSecs: 0,
                            channels: {},
                            categories: {},
                            activeUsersMap: {},
                            teamUtil: {},
                            userUtil: {}
                        };
                    }
                    const m = merged[date];
                    m.sessionsCount += data.sessionsCount || 0;
                    m.scoredSessionsCount += data.scoredSessionsCount || 0;
                    m.totalScore += data.totalScore || 0;
                    m.totalDurationSecs += data.totalDurationSecs || 0;
                    
                    if (data.channels) {
                        Object.entries(data.channels).forEach(([ch, v]: [string, any]) => {
                            if (!m.channels[ch]) m.channels[ch] = { sessionsCount: 0, scoredSessionsCount: 0, totalScore: 0, totalDurationSecs: 0 };
                            m.channels[ch].sessionsCount += v.sessionsCount || 0;
                            m.channels[ch].scoredSessionsCount += v.scoredSessionsCount || 0;
                            m.channels[ch].totalScore += v.totalScore || 0;
                            m.channels[ch].totalDurationSecs += v.totalDurationSecs || 0;
                        });
                    }

                    if (data.categories) {
                        Object.entries(data.categories).forEach(([cat, v]: [string, any]) => {
                            if (!m.categories[cat]) m.categories[cat] = { count: 0, totalScore: 0 };
                            m.categories[cat].count += v.count || 0;
                            m.categories[cat].totalScore += v.totalScore || 0;
                        });
                    }

                    if (type === 'global') {
                        Object.assign(m.teamUtil, data.teamUtil || {});
                        Object.assign(m.userUtil, data.userUtil || {});
                        (data.activeUsers || []).forEach((u: string) => m.activeUsersMap[u] = true);
                    }
                });
                
                const finalAggs = Object.values(merged).sort((a: any, b: any) => b.date.localeCompare(a.date));
                setAggregates(finalAggs);
            } catch (e: any) {
                if (!cancelled) setError(e.message || 'Failed to load analytics data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [startDate, endDate, teamFilters, userFilter, retryTrigger]);

    /* ── Admin actions ──────────────────────────────────── */
    const handleAction = async (uid: string, action: 'DELETE_USER' | 'RESET_PASSWORD' | 'MAKE_ADMIN' | 'REVOKE_ADMIN') => {
        if (!window.confirm(`Confirm: ${action.replace(/_/g, ' ')}?`)) return;
        setActionLoading(uid);
        try {
            let resData: any;
            try {
                const res = await callFunctionWithFallback<any, any>('adminUserAction', { action, targetUid: uid });
                resData = res.data;
            } catch (err) {
                if (action === 'MAKE_ADMIN' || action === 'REVOKE_ADMIN') {
                    const isNowAdmin = action === 'MAKE_ADMIN';
                    await setDoc(doc(db, 'users', uid), { isAdmin: isNowAdmin }, { merge: true });
                    resData = { success: true, message: isNowAdmin ? 'User granted admin rights.' : 'Admin rights revoked.' };
                } else {
                    throw err;
                }
            }
            if (resData?.success) {
                alert(resData.message || 'Done');
                if (action === 'DELETE_USER')   setUsers(p => p.filter(u => u.uid !== uid));
                if (action === 'MAKE_ADMIN')    setUsers(p => p.map(u => u.uid === uid ? { ...u, isAdmin: true }  : u));
                if (action === 'REVOKE_ADMIN')  setUsers(p => p.map(u => u.uid === uid ? { ...u, isAdmin: false } : u));
            }
        } catch (e: any) { alert(e?.message || 'Action failed.'); }
        finally    { setActionLoading(null); }
    };

    const handleUpdateAllocation = async () => {
        if (!editingAllocationUserId) return;
        setActionLoading('updating_allocation');
        try {
            let allocatedMinutes: number | 'unlimited' = 'unlimited';
            if (editAllocationValue !== 'unlimited') {
                allocatedMinutes = parseInt(editAllocationValue, 10);
                if (isNaN(allocatedMinutes) || allocatedMinutes < 0) {
                    throw new Error("Invalid minutes value");
                }
            }
            try {
                await callFunctionWithFallback('adminUpdateUserAllocation', { targetUid: editingAllocationUserId, allocatedMinutes });
            } catch {
                await setDoc(doc(db, 'users', editingAllocationUserId), { allocatedMinutes }, { merge: true });
            }
            setUsers(prev => prev.map(u => u.uid === editingAllocationUserId ? { ...u, allocatedMinutes } : u));
            setEditingAllocationUserId(null);
            setEditAllocationValue('');
        } catch (e: any) {
            alert(e.message || 'Failed to update allocation.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleUserGuide = async (uid: string, currentDisabled?: boolean) => {
        const nextDisabled = !currentDisabled;
        setActionLoading(uid);
        try {
            await setDoc(doc(db, 'users', uid), { conversationGuideDisabled: nextDisabled }, { merge: true });
            setUsers(prev => prev.map(u => u.uid === uid ? { ...u, conversationGuideDisabled: nextDisabled } : u));
        } catch (e: any) {
            alert(e?.message || 'Failed to update Conversation Guide setting.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleBulkSetGuide = async (disableGuide: boolean, targetList: AdminUser[]) => {
        if (targetList.length === 0) return;
        const scopeDesc = selectedUserIds.size > 0
            ? `${targetList.length} selected user(s)`
            : `team "${userMgmtTeamFilter}" (${targetList.length} user(s))`;
        if (!window.confirm(`Confirm: ${disableGuide ? 'Disable' : 'Enable'} Conversation Guide for ${scopeDesc}?`)) return;

        setActionLoading('bulk_guide');
        try {
            const CHUNK_SIZE = 400;
            for (let i = 0; i < targetList.length; i += CHUNK_SIZE) {
                const chunk = targetList.slice(i, i + CHUNK_SIZE);
                const batch = writeBatch(db);
                chunk.forEach(u => {
                    batch.set(doc(db, 'users', u.uid), { conversationGuideDisabled: disableGuide }, { merge: true });
                });
                await batch.commit();
            }

            if (selectedUserIds.size === 0 && userMgmtTeamFilter !== 'All') {
                try {
                    await setDoc(doc(db, 'teamSettings', encodeURIComponent(userMgmtTeamFilter)), {
                        teamName: userMgmtTeamFilter,
                        conversationGuideDisabled: disableGuide,
                        updatedAt: new Date().toISOString()
                    }, { merge: true });
                } catch {
                    // Non-fatal if teamSettings rules not yet deployed
                }
            }

            const targetSet = new Set(targetList.map(u => u.uid));
            setUsers(prev => prev.map(u => targetSet.has(u.uid) ? { ...u, conversationGuideDisabled: disableGuide } : u));
            setSelectedUserIds(new Set());
        } catch (e: any) {
            alert(e?.message || 'Failed to bulk update Conversation Guide settings.');
        } finally {
            setActionLoading(null);
        }
    };

    const userMgmtTeams = useMemo(() => {
        const counts = new Map<string, number>();
        users.forEach(u => {
            const t = (u.team || '').trim();
            if (t) counts.set(t, (counts.get(t) || 0) + 1);
        });
        return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [users]);

    /* ── Derived lists for filter dropdowns ─────────────── */
    // Collect unique teams and users from aggregate userUtil / teamUtil maps
    const allTeams = useMemo(() => {
        const s = new Set<string>();
        users.forEach(u => { if (u.team) s.add(u.team); });
        // Fallback to aggregates if users list is empty or doesn't have all teams
        aggregates.forEach(a => { if (a.teamUtil) Object.keys(a.teamUtil).filter(k => k !== 'Unknown').forEach(k => s.add(k)); });
        return Array.from(s);
    }, [users, aggregates]);

    const allUsers = useMemo(() => {
        const s = new Set<string>();
        users.forEach(u => s.add(u.fullName || u.email));
        aggregates.forEach(a => { if (a.userUtil) Object.keys(a.userUtil).filter(k => k !== 'Unknown').forEach(k => s.add(k)); });
        return Array.from(s);
    }, [users, aggregates]);

    const allChannels = useMemo(() => {
        const s = new Set<string>();
        aggregates.forEach(a => {
            if (a.channels) Object.keys(a.channels).forEach(k => s.add(k));
        });
        // Default channels if none found yet
        if (s.size === 0) { s.add('Voice'); s.add('Text'); }
        return Array.from(s);
    }, [aggregates]);

    /* ── Filter aggregates to the KPI date window ────────── */
    const kpiAggregates = useMemo(() => {
        let r = aggregates;
        if (startDate) r = r.filter(a => a.date >= startDate);
        if (endDate)   r = r.filter(a => a.date <= endDate);
        return r;
    }, [aggregates, startDate, endDate]);

    /* ── KPI ────────────────────────────────────────────── */
    const kpi = useMemo(() => {
        let totalScore = 0, scoredSessions = 0, totalSessions = 0, totalSecs = 0;
        const uniqueUsers = new Set<string>();
        
        kpiAggregates.forEach(a => {
            if (channelFilters.length > 0) {
                // Sum only selected channels
                channelFilters.forEach(ch => {
                    const c = a.channels?.[ch];
                    if (c) {
                        totalSessions  += c.sessionsCount || 0;
                        scoredSessions += c.scoredSessionsCount || 0;
                        totalScore     += c.totalScore || 0;
                        totalSecs      += c.totalDurationSecs || 0;
                    }
                });
            } else {
                // Use totals (already filtered by Team/User in the fetch layer)
                totalSessions  += a.sessionsCount || 0;
                scoredSessions += a.scoredSessionsCount || 0;
                totalScore     += a.totalScore || 0;
                totalSecs      += a.totalDurationSecs || 0;
            }

            // Unique users logic
            if (a.activeUsersMap) {
                Object.keys(a.activeUsersMap).forEach(u => uniqueUsers.add(u));
            }
        });
        
        const noScore = totalSessions - scoredSessions;
        return {
            avg:   scoredSessions ? (totalScore / scoredSessions).toFixed(1) : '—',
            count: scoredSessions,
            noScore,
            totalSecs,
            users: userFilter !== 'All' ? 1 : uniqueUsers.size,
        };
    }, [kpiAggregates, channelFilters, userFilter]);

    /* ── Score trend ────────────────────────────────────── */
    const trendData = useMemo(() => {
        try {
            const endObj = new Date((endDate || getToday()) + 'T23:59:59');
            const count = CAPS[timeframe];
            const buckets: { start: Date; end: Date; label: string }[] = [];

            for (let i = 0; i < count; i++) {
                let bStart: Date, bEnd: Date, label: string;
                if (timeframe === 'daily') {
                    bEnd = new Date(endObj); bEnd.setDate(endObj.getDate() - i);
                    bStart = new Date(bEnd); bStart.setHours(0, 0, 0, 0);
                    label = bEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                } else if (timeframe === 'weekly') {
                    bEnd = new Date(endObj); bEnd.setDate(endObj.getDate() - (i * 7));
                    bStart = new Date(bEnd); bStart.setDate(bStart.getDate() - 6);
                    bStart.setHours(0, 0, 0, 0);
                    label = `${bStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${bEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                } else {
                    bEnd = new Date(endObj.getFullYear(), endObj.getMonth() - i + 1, 0);
                    bStart = new Date(endObj.getFullYear(), endObj.getMonth() - i, 1);
                    bStart.setHours(0, 0, 0, 0);
                    label = bStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                }
                buckets.push({ start: bStart, end: bEnd, label });
            }
            buckets.reverse();

            return buckets.map(b => {
                let tot = 0, cnt = 0;
                aggregates.forEach(a => {
                    const d = new Date(a.date + 'T00:00:00');
                    if (d >= b.start && d <= b.end) {
                        if (channelFilters.length > 0) {
                            channelFilters.forEach(ch => {
                                const c = a.channels?.[ch];
                                if (c && c.scoredSessionsCount) {
                                    tot += c.totalScore;
                                    cnt += c.scoredSessionsCount;
                                }
                            });
                        } else {
                            if (a.scoredSessionsCount > 0) {
                                tot += a.totalScore;
                                cnt += a.scoredSessionsCount;
                            }
                        }
                    }
                });
                return { name: b.label, score: cnt > 0 ? Math.round(tot / cnt) : null };
            });
        } catch { return []; }
    }, [aggregates, timeframe, endDate, channelFilters]);

    /* ── Channel performance ────────────────────────────── */
    const channelData = useMemo(() => {
        try {
            const map: Record<string, { tot: number; cnt: number }> = {};
            kpiAggregates.forEach(a => {
                const channels = a.channels || {};
                Object.entries(channels).forEach(([ch, v]: [string, any]) => {
                    if (!map[ch]) map[ch] = { tot: 0, cnt: 0 };
                    map[ch].cnt += v.sessionsCount || 0;
                    map[ch].tot += v.totalScore || 0;
                });
            });
            return Object.keys(map).filter(k => map[k].cnt > 0)
                .map(k => ({ name: k, score: Math.round(map[k].tot / map[k].cnt), sessions: map[k].cnt }));
        } catch { return []; }
    }, [kpiAggregates]);

    /* ── Category radar ─────────────────────────────────── */
    const CATS = [
        { key: 'greeting_and_opening',              label: 'Opening'         },
        { key: 'active_listening_and_understanding', label: 'Listening'       },
        { key: 'empathy_and_tone',                  label: 'Empathy'         },
        { key: 'problem_solving_resolution',        label: 'Problem Solving' },
        { key: 'communication_clarity',             label: 'Clarity'         },
        { key: 'compliance_and_accuracy',           label: 'Compliance'      },
        { key: 'closing',                           label: 'Closing'         },
    ];
    const categoryData = useMemo(() => {
        try {
            const map: Record<string, { tot: number; cnt: number }> = {};
            CATS.forEach(c => (map[c.key] = { tot: 0, cnt: 0 }));
            kpiAggregates.forEach(a => {
                const cats = a.categories || {};
                CATS.forEach(c => {
                    const v = cats[c.key];
                    if (v && v.count > 0) { map[c.key].tot += v.totalScore; map[c.key].cnt += v.count; }
                });
            });
            // The max score per category is 10.
            // (Average score out of 10) / 10 * 100 = Average score * 10
            return CATS.map(c => ({ name: c.label, score: map[c.key].cnt ? Math.round((map[c.key].tot / map[c.key].cnt) * 10) : 0 }));
        } catch { return CATS.map(c => ({ name: c.label, score: 0 })); }
    }, [kpiAggregates]);

    /* ── Top / bottom performers ────────────────────────── */
    const performers = useMemo(() => {
        try {
            const map: Record<string, { tot: number; cnt: number; secs: number }> = {};
            kpiAggregates.forEach(a => {
                const util = a.userUtil || {};
                Object.entries(util).forEach(([name, v]: [string, any]) => {
                    if (!map[name]) map[name] = { tot: 0, cnt: 0, secs: 0 };
                    map[name].secs += v.secs || 0;
                    map[name].tot  += v.scoreTot || 0;
                    map[name].cnt  += v.scoreCnt || 0;
                });
            });
            const ranked = Object.entries(map)
                .filter(([, v]) => v.cnt > 0)
                .map(([name, v]) => ({ name, avg: +(v.tot / v.cnt).toFixed(1), secs: v.secs }))
                .sort((a, b) => b.avg - a.avg);
            return { top: ranked.slice(0, 5), bottom: ranked.slice(-5).reverse() };
        } catch { return { top: [], bottom: [] }; }
    }, [kpiAggregates]);

    /* ── Strengths / improvements ───────────────────────── */
    const insights = useMemo(() => {
        try {
            const map: Record<string, { tot: number; cnt: number }> = {};
            CATS.forEach(c => (map[c.key] = { tot: 0, cnt: 0 }));
            kpiAggregates.forEach(a => {
                const cats = a.categories || {};
                CATS.forEach(c => {
                    const v = cats[c.key];
                    if (v && v.count > 0) {
                        map[c.key].tot += v.totalScore;
                        map[c.key].cnt += v.count;
                    }
                });
            });

            const categoryStats = CATS.map(c => {
                const avg = map[c.key].cnt ? (map[c.key].tot / map[c.key].cnt) * 10 : 0;
                return {
                    key: c.key,
                    label: c.label,
                    avg: Math.round(avg),
                    count: map[c.key].cnt
                };
            }).filter(c => c.count > 0);

            const sortedByAvg = [...categoryStats].sort((a, b) => b.avg - a.avg);
            
            return {
                strengths: sortedByAvg.slice(0, 3),
                improvements: [...sortedByAvg].reverse().slice(0, 3)
            };
        } catch {
            return { strengths: [], improvements: [] };
        }
    }, [kpiAggregates]);

    /* ── Utilization ────────────────────────────────────── */
    const utilization = useMemo(() => {
        try {
            const teamMap: Record<string, { sessions: number; secs: number; scoreTot: number; scoreCnt: number }> = {};
            const agentMap: Record<string, { sessions: number; secs: number; scoreTot: number; scoreCnt: number }> = {};
            kpiAggregates.forEach(a => {
                Object.entries(a.teamUtil || {}).forEach(([name, v]: [string, any]) => {
                    if (!teamMap[name]) teamMap[name] = { sessions: 0, secs: 0, scoreTot: 0, scoreCnt: 0 };
                    teamMap[name].sessions += v.sessions || 0;
                    teamMap[name].secs     += v.secs || 0;
                    teamMap[name].scoreTot += v.scoreTot || 0;
                    teamMap[name].scoreCnt += v.scoreCnt || 0;
                });
                Object.entries(a.userUtil || {}).forEach(([name, v]: [string, any]) => {
                    if (!agentMap[name]) agentMap[name] = { sessions: 0, secs: 0, scoreTot: 0, scoreCnt: 0 };
                    agentMap[name].sessions += v.sessions || 0;
                    agentMap[name].secs     += v.secs || 0;
                    agentMap[name].scoreTot += v.scoreTot || 0;
                    agentMap[name].scoreCnt += v.scoreCnt || 0;
                });
            });
            const topTeams = Object.entries(teamMap)
                .map(([name, v]) => ({ name, sessions: v.sessions, secs: v.secs, avg: v.scoreCnt > 0 ? +(v.scoreTot / v.scoreCnt).toFixed(1) : 0 }))
                .sort((a, b) => b.sessions - a.sessions).slice(0, 5);
            const topAgents = Object.entries(agentMap)
                .map(([name, v]) => ({ name, sessions: v.sessions, secs: v.secs, avg: v.scoreCnt > 0 ? +(v.scoreTot / v.scoreCnt).toFixed(1) : 0 }))
                .sort((a, b) => b.sessions - a.sessions).slice(0, 10);
            return { topTeams, topAgents };
        } catch { return { topTeams: [], topAgents: [] }; }
    }, [kpiAggregates]);

    /* ── User list filter ───────────────────────────────── */
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            if (userMgmtTeamFilter !== 'All' && (u.team || '').trim() !== userMgmtTeamFilter) {
                return false;
            }
            return (`${u.fullName} ${u.email} ${u.team}`).toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [users, userMgmtTeamFilter, searchTerm]);

    const bulkTargetUsers = useMemo(() => {
        if (selectedUserIds.size > 0) {
            return users.filter(u => selectedUserIds.has(u.uid));
        }
        if (userMgmtTeamFilter !== 'All') {
            return filteredUsers;
        }
        return [];
    }, [users, selectedUserIds, userMgmtTeamFilter, filteredUsers]);

    const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedUserIds.has(u.uid));

    /* ── Early renders ──────────────────────────────────── */
    if (loading) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: '1rem' }}>
            <div style={{ width: '2.2rem', height: '2.2rem', border: '3px solid #F1F3F5', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ color: '#9CA3AF', fontSize: '0.88rem' }}>Loading…</p>
        </div>
    );
    if (error) return (
        <div className="card" style={{ textAlign: 'center', padding: '4rem' }}>
            <ShieldAlert size={40} color="var(--danger)" style={{ margin: '0 auto 1rem' }} />
            <h3 style={{ color: 'var(--danger)', marginBottom: '0.5rem' }}>Admin Error</h3>
            <p style={{ color: '#6B7280' }}>{error}</p>
            <button className="button button-primary" style={{ marginTop: '1.5rem' }} onClick={() => setRetryTrigger(prev => prev + 1)}>Retry</button>
        </div>
    );

    /* ── Style helpers ──────────────────────────────────── */
    const tabStyle = (active: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: '0.45rem',
        padding: '0.55rem 1.15rem', borderRadius: '0.6rem',
        fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer',
        border: 'none', fontFamily: 'inherit', transition: 'all 0.18s ease',
        background: active ? 'var(--primary)' : 'transparent',
        color:      active ? 'white' : '#9CA3AF',
        boxShadow:  active ? '0 2px 8px rgba(160,43,147,0.2)' : 'none',
    });

    const tfStyle = (active: boolean): React.CSSProperties => ({
        padding: '0.3rem 0.8rem', borderRadius: '0.45rem',
        fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
        border: 'none', fontFamily: 'inherit', transition: 'all 0.15s ease',
        background: active ? 'var(--primary)' : 'transparent',
        color:      active ? 'white' : '#9CA3AF',
    });

    const iconBtn = (color: string): React.CSSProperties => ({
        width: '1.9rem', height: '1.9rem', borderRadius: '0.45rem', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}14`, color, cursor: 'pointer', transition: 'all 0.15s ease',
    });

    const pillClass = (avg: number) => avg >= 86 ? 'pill-high' : avg >= 71 ? 'pill-mid' : 'pill-low';

    /* ═════════════════ RENDER ══════════════════════════════ */
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'fadeIn 0.35s ease-out' }}>

            {/* Tab Bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: '1rem', padding: '0.4rem 0.5rem', border: '1px solid #F1F3F5', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', gap: '0.2rem' }}>
                    {([
                        { id: 'users', label: 'User Management', icon: Users },
                        { id: 'analytics', label: 'Team Analytics', icon: BarChart2 },
                        { id: 'content', label: 'Content Management', icon: FileText },
                    ] as const).map(t => (
                        <button key={t.id} style={tabStyle(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>
                            <t.icon size={14} />{t.label}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {activeTab === 'analytics' && (
                        <div style={{ display: 'flex', gap: '0.1rem', background: '#F9FAFB', borderRadius: '0.55rem', padding: '0.22rem' }}>
                            {(['daily', 'weekly', 'monthly'] as Timeframe[]).map(tf => (
                                <button key={tf} style={tfStyle(timeframe === tf)} onClick={() => setTimeframe(tf)}>
                                    {tf.charAt(0).toUpperCase() + tf.slice(1)}
                                </button>
                            ))}
                        </div>
                    )}
                    <button
                        onClick={() => setShowAssistant(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                            padding: '0.45rem 0.8rem', borderRadius: '0.55rem',
                            fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                            border: 'none', background: 'rgba(249, 115, 22, 0.1)', color: '#F97316',
                            fontFamily: 'inherit', transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(249, 115, 22, 0.2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(249, 115, 22, 0.1)'}
                    >
                        <Bot size={14} /> Simulate AI
                    </button>
                </div>
            </div>

            {/* ══════════ CONTENT MANAGEMENT ═════════════════════ */}
            {activeTab === 'content' && <AdminContentManagement />}

            {/* ══════════ USER MANAGEMENT ══════════════════════ */}
            {activeTab === 'users' && (
                <div style={{ background: 'white', borderRadius: '1rem', border: '1px solid #F1F3F5', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    {/* Filter & Scoped Conversation Guide Bulk Bar */}
                    <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #F9FAFB', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem', alignItems: 'center', flex: '1 1 420px' }}>
                            <div style={{ position: 'relative', minWidth: '250px', flex: '1 1 250px', maxWidth: '340px' }}>
                                <Search size={14} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#D1D5DB', pointerEvents: 'none' }} />
                                <input className="input-fancy" style={{ paddingLeft: '2.35rem', height: '2.35rem', fontSize: '0.85rem' }}
                                    placeholder="Search name, email, or team…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>

                            <select
                                className="input-fancy"
                                style={{ height: '2.35rem', fontSize: '0.85rem', width: 'auto', minWidth: '190px', padding: '0 0.85rem' }}
                                value={userMgmtTeamFilter}
                                onChange={e => {
                                    setUserMgmtTeamFilter(e.target.value);
                                    setSelectedUserIds(new Set());
                                }}
                                title="Filter by Team / Group"
                            >
                                <option value="All">All Teams / Groups ({users.length})</option>
                                {userMgmtTeams.map(([tName, count]) => (
                                    <option key={tName} value={tName}>{tName} ({count})</option>
                                ))}
                            </select>

                            {selectedUserIds.size > 0 && (
                                <button
                                    onClick={() => setSelectedUserIds(new Set())}
                                    style={{ background: '#F3F4F6', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.7rem', fontSize: '0.76rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer' }}
                                >
                                    Clear Selection ({selectedUserIds.size})
                                </button>
                            )}
                        </div>

                        {/* Scoped Conversation Guide Bulk Action Panel */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', background: '#FAFAFA', padding: '0.45rem 0.75rem', borderRadius: '0.65rem', border: '1px solid #F1F3F5' }}>
                            <Lightbulb size={15} color="var(--primary)" />
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                                Conversation Guide:
                            </span>
                            {bulkTargetUsers.length > 0 ? (
                                <>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)', background: 'rgba(160,43,147,0.1)', padding: '0.18rem 0.55rem', borderRadius: '999px' }}>
                                        {selectedUserIds.size > 0
                                            ? `${selectedUserIds.size} Selected`
                                            : `Team: ${userMgmtTeamFilter} (${bulkTargetUsers.length})`}
                                    </span>
                                    <button
                                        disabled={actionLoading === 'bulk_guide'}
                                        onClick={() => handleBulkSetGuide(false, bulkTargetUsers)}
                                        style={{
                                            padding: '0.32rem 0.65rem', borderRadius: '0.45rem', border: '1px solid #A7F3D0',
                                            background: '#ECFDF5', color: '#059669', fontSize: '0.75rem', fontWeight: 700,
                                            cursor: 'pointer', transition: 'all 0.15s ease'
                                        }}
                                        title="Enable Conversation Guide for this group/selection"
                                    >
                                        Enable for {selectedUserIds.size > 0 ? 'Selected' : 'Team'}
                                    </button>
                                    <button
                                        disabled={actionLoading === 'bulk_guide'}
                                        onClick={() => handleBulkSetGuide(true, bulkTargetUsers)}
                                        style={{
                                            padding: '0.32rem 0.65rem', borderRadius: '0.45rem', border: '1px solid #FECACA',
                                            background: '#FEF2F2', color: '#DC2626', fontSize: '0.75rem', fontWeight: 700,
                                            cursor: 'pointer', transition: 'all 0.15s ease'
                                        }}
                                        title="Disable Conversation Guide for this group/selection"
                                    >
                                        Disable for {selectedUserIds.size > 0 ? 'Selected' : 'Team'}
                                    </button>
                                </>
                            ) : (
                                <span style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                                    Select a Team or check users to toggle by group
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ width: '100%', overflowX: 'hidden' }}>
                        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <colgroup>
                                <col style={{ width: '36px' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '23%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '96px' }} />
                                <col style={{ width: '92px' }} />
                                <col style={{ width: '108px' }} />
                                <col style={{ width: '154px' }} />
                            </colgroup>
                            <thead>
                                <tr style={{ background: '#FAFAFA' }}>
                                    <th style={{ padding: '0.6rem 0.35rem 0.6rem 0.85rem', borderBottom: '1px solid #F1F3F5', textAlign: 'left' }}>
                                        <input
                                            type="checkbox"
                                            checked={allFilteredSelected}
                                            onChange={() => {
                                                if (allFilteredSelected) {
                                                    const next = new Set(selectedUserIds);
                                                    filteredUsers.forEach(u => next.delete(u.uid));
                                                    setSelectedUserIds(next);
                                                } else {
                                                    const next = new Set(selectedUserIds);
                                                    filteredUsers.forEach(u => next.add(u.uid));
                                                    setSelectedUserIds(next);
                                                }
                                            }}
                                            title="Select all visible users"
                                            style={{ cursor: 'pointer', accentColor: 'var(--primary)' }}
                                        />
                                    </th>
                                    {['Name', 'Email', 'Team', 'Conv. Guide', 'Last Login', 'Usage / Alloc.', 'Actions'].map((h) => (
                                        <th key={h} style={{ padding: '0.6rem 0.5rem', paddingRight: h === 'Actions' ? '0.85rem' : '0.5rem', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.66rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #F1F3F5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(u => {
                                    const currentMonth = new Date().toISOString().slice(0, 7);
                                    const rawUsed = Number(u.usedMinutes?.[currentMonth] ?? 0);
                                    const used = Number.isFinite(rawUsed) ? rawUsed : 0;
                                    const alloc = u.allocatedMinutes === undefined ? 150 : u.allocatedMinutes;
                                    const usageStr = alloc === 'unlimited' ? `${used.toFixed(2)} / ∞` : `${used.toFixed(2)} / ${alloc}`;
                                    const isExceeded = alloc !== 'unlimited' && used >= alloc;
                                    const isSelected = selectedUserIds.has(u.uid);
                                    const guideDisabled = u.conversationGuideDisabled === true;
                                    const displayName = u.fullName || (u.email ? u.email.split('@')[0] : 'User');

                                    return (
                                        <tr key={u.uid} style={{ borderBottom: '1px solid #F9FAFB', background: isSelected ? 'rgba(160,43,147,0.03)' : 'transparent' }}
                                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#FAFAFA'; }}
                                            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                                        <td style={{ padding: '0.65rem 0.35rem 0.65rem 0.85rem' }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {
                                                    const next = new Set(selectedUserIds);
                                                    if (next.has(u.uid)) next.delete(u.uid);
                                                    else next.add(u.uid);
                                                    setSelectedUserIds(next);
                                                }}
                                                style={{ cursor: 'pointer', accentColor: 'var(--primary)' }}
                                            />
                                        </td>
                                        <td style={{ padding: '0.65rem 0.5rem', overflow: 'hidden' }} title={displayName}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0 }}>
                                                <div style={{ width: '1.65rem', height: '1.65rem', borderRadius: '50%', background: 'rgba(160,43,147,0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.66rem', fontWeight: 800, flexShrink: 0, textTransform: 'uppercase' }}>
                                                    {(u.fullName || u.email || '?').charAt(0)}
                                                </div>
                                                <span style={{ fontWeight: 600, color: '#1F2937', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                                                {u.isAdmin && <span style={{ fontSize: '0.55rem', fontWeight: 700, padding: '0.08rem 0.38rem', borderRadius: '2rem', background: 'rgba(160,43,147,0.1)', color: 'var(--primary)', flexShrink: 0 }}>ADMIN</span>}
                                            </div>
                                        </td>

                                        <td style={{ padding: '0.65rem 0.5rem', color: '#6B7280', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.email}>{u.email}</td>
                                        <td style={{ padding: '0.65rem 0.5rem', color: '#4B5563', fontSize: '0.78rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.team || ''}>{u.team || '—'}</td>
                                        <td style={{ padding: '0.65rem 0.5rem' }}>
                                            <button
                                                disabled={!!actionLoading}
                                                onClick={() => handleToggleUserGuide(u.uid, guideDisabled)}
                                                title={guideDisabled ? 'Click to Enable Conversation Guide for this user' : 'Click to Disable Conversation Guide for this user'}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '0.28rem',
                                                    padding: '0.18rem 0.5rem', borderRadius: '999px',
                                                    fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                                                    border: guideDisabled ? '1px solid #FECACA' : '1px solid #A7F3D0',
                                                    background: guideDisabled ? '#FEF2F2' : '#ECFDF5',
                                                    color: guideDisabled ? '#DC2626' : '#059669',
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <Lightbulb size={10} />
                                                {guideDisabled ? 'Disabled' : 'Enabled'}
                                            </button>
                                        </td>
                                        <td style={{ padding: '0.65rem 0.5rem', color: '#9CA3AF', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                            {u.lastSignInTime ? new Date(u.lastSignInTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                                        </td>
                                        <td style={{ padding: '0.65rem 0.5rem', color: isExceeded ? '#EF4444' : '#6B7280', fontSize: '0.76rem', fontWeight: isExceeded ? 600 : 400, whiteSpace: 'nowrap' }}>
                                            {usageStr}m
                                        </td>
                                        <td style={{ padding: '0.65rem 0.85rem 0.65rem 0.4rem' }}>
                                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                                                <button style={{ ...iconBtn('#8B5CF6'), width: '1.65rem', height: '1.65rem' }} title="Edit Minutes Allocation" disabled={!!actionLoading} onClick={() => {
                                                    setEditingAllocationUserId(u.uid);
                                                    setEditAllocationValue(String(u.allocatedMinutes === undefined ? 150 : u.allocatedMinutes));
                                                }}>
                                                    <Clock size={12} />
                                                </button>
                                                <button style={{ ...iconBtn(u.isAdmin ? '#F59E0B' : '#10B981'), width: '1.65rem', height: '1.65rem' }} title={u.isAdmin ? 'Revoke Admin' : 'Grant Admin'} disabled={!!actionLoading} onClick={() => handleAction(u.uid, u.isAdmin ? 'REVOKE_ADMIN' : 'MAKE_ADMIN')}>
                                                    <Shield size={12} />
                                                </button>
                                                <button style={{ ...iconBtn('var(--primary)'), width: '1.65rem', height: '1.65rem' }} title="View Analytics" onClick={() => {
                                                    const emailPrefix = u.email ? u.email.split('@')[0] : '';
                                                    const candidates = [u.fullName, u.email, emailPrefix].filter(Boolean) as string[];
                                                    const matched = allUsers.find(id => candidates.includes(id)) || u.email || 'Unknown';
                                                    setUserFilter(matched);
                                                    setTeamFilters([]);
                                                    setActiveTab('analytics');
                                                }}>
                                                    <BarChart2 size={12} />
                                                </button>
                                                <button style={{ ...iconBtn('#3B82F6'), width: '1.65rem', height: '1.65rem' }} title="Reset Password" disabled={!!actionLoading} onClick={() => handleAction(u.uid, 'RESET_PASSWORD')}>
                                                    <Mail size={12} />
                                                </button>
                                                <button style={{ ...iconBtn('#EF4444'), width: '1.65rem', height: '1.65rem' }} title="Delete User" disabled={!!actionLoading} onClick={() => handleAction(u.uid, 'DELETE_USER')}>
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )})}
                                {filteredUsers.length === 0 && (
                                    <tr><td colSpan={8} style={{ padding: '3.5rem', textAlign: 'center', color: '#D1D5DB', fontSize: '0.875rem' }}>No users match your search.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ══════════ ANALYTICS FILTERS ══════════ */}
            {activeTab === 'analytics' && (
                <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'center', background: 'white', borderRadius: '1rem', padding: '0.85rem 1.15rem', border: '1px solid #F1F3F5', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    {/* Team Multi-Select */}
                    <div style={{ position: 'relative' }}>
                        <div 
                            className="input-fancy" 
                            style={{ height: '2.2rem', fontSize: '0.84rem', minWidth: '180px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                            onClick={() => setTeamMenuOpen(!teamMenuOpen)}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {teamFilters.length === 0 ? 'All Teams' : `${teamFilters.length} Team${teamFilters.length > 1 ? 's' : ''} Selected`}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>▼</span>
                        </div>

                        {teamMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.35rem', background: 'white', border: '1px solid #E5E7EB', borderRadius: '0.65rem', width: '240px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                
                                <div style={{ display: 'flex', padding: '0.55rem', borderBottom: '1px solid #F3F4F6', gap: '0.35rem' }}>
                                    <input 
                                        placeholder="Search Teams..." 
                                        value={teamSearch} 
                                        onChange={e => setTeamSearch(e.target.value)} 
                                        style={{ flex: 1, padding: '0.35rem 0.65rem', fontSize: '0.75rem', borderRadius: '0.35rem', border: '1px solid #E5E7EB', outline: 'none' }} 
                                    />
                                    <button 
                                        style={{ background: '#F3F4F6', border: 'none', borderRadius: '0.35rem', padding: '0 0.5rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, color: '#4B5563' }}
                                        onClick={() => setTeamSortAsc(!teamSortAsc)}
                                        title="Sort Alphabetically"
                                    >
                                        {teamSortAsc ? 'A-Z' : 'Z-A'}
                                    </button>
                                </div>

                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {(() => {
                                        const sorted = [...allTeams]
                                            .filter(t => t.toLowerCase().includes(teamSearch.toLowerCase()))
                                            .sort((a, b) => teamSortAsc ? a.localeCompare(b) : b.localeCompare(a));
                                        
                                        if (sorted.length === 0) return <div style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center' }}>No teams found</div>;
                                        
                                        return sorted.map(t => {
                                            const isSel = teamFilters.includes(t);
                                            return (
                                                <div 
                                                    key={t}
                                                    onClick={() => {
                                                        setTeamFilters(prev => 
                                                            isSel ? prev.filter(x => x !== t) : [...prev, t]
                                                        );
                                                        setUserFilter('All'); // Reset user filter if team changes
                                                    }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', cursor: 'pointer', background: isSel ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                                >
                                                    <input type="checkbox" checked={isSel} readOnly style={{ cursor: 'pointer' }} />
                                                    <span style={{ fontSize: '0.8rem', color: isSel ? 'var(--primary)' : '#374151', fontWeight: isSel ? 600 : 400 }}>{t}</span>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                <div style={{ padding: '0.55rem', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', background: '#F9FAFB' }}>
                                    <button 
                                        style={{ fontSize: '0.75rem', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                        onClick={() => { setTeamFilters([]); setUserFilter('All'); }}
                                    >
                                        Clear
                                    </button>
                                    <button 
                                        style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                                        onClick={() => setTeamMenuOpen(false)}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div 
                            className="input-fancy" 
                            style={{ height: '2.2rem', fontSize: '0.84rem', minWidth: '150px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {userFilter === 'All' ? 'All Users' : userFilter}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>▼</span>
                        </div>

                        {userMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.35rem', background: 'white', border: '1px solid #E5E7EB', borderRadius: '0.65rem', width: '240px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                
                                <div style={{ display: 'flex', padding: '0.55rem', borderBottom: '1px solid #F3F4F6', gap: '0.35rem' }}>
                                    <input 
                                        placeholder="Search Users..." 
                                        value={userSearch} 
                                        onChange={e => setUserSearch(e.target.value)} 
                                        style={{ flex: 1, padding: '0.35rem 0.65rem', fontSize: '0.75rem', borderRadius: '0.35rem', border: '1px solid #E5E7EB', outline: 'none' }} 
                                    />
                                    <button 
                                        style={{ background: '#F3F4F6', border: 'none', borderRadius: '0.35rem', padding: '0 0.5rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, color: '#4B5563' }}
                                        onClick={() => setUserSortAsc(!userSortAsc)}
                                        title="Sort Alphabetically"
                                    >
                                        {userSortAsc ? 'A-Z' : 'Z-A'}
                                    </button>
                                </div>

                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <div 
                                        onClick={() => {
                                            setUserFilter('All');
                                            setUserMenuOpen(false);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', cursor: 'pointer', background: userFilter === 'All' ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                    >
                                        <span style={{ fontSize: '0.8rem', color: userFilter === 'All' ? 'var(--primary)' : '#374151', fontWeight: userFilter === 'All' ? 600 : 400 }}>All Users</span>
                                    </div>
                                    {(() => {
                                        const sorted = [...allUsers]
                                            .filter(u => u.toLowerCase().includes(userSearch.toLowerCase()))
                                            .sort((a, b) => userSortAsc ? a.localeCompare(b) : b.localeCompare(a));
                                        
                                        if (userFilter !== 'All' && !allUsers.includes(userFilter) && userFilter.toLowerCase().includes(userSearch.toLowerCase())) {
                                            sorted.unshift(userFilter);
                                        }
                                        
                                        if (sorted.length === 0) return <div style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center' }}>No users found</div>;
                                        
                                        return sorted.map(u => {
                                            const isSel = userFilter === u;
                                            return (
                                                <div 
                                                    key={u}
                                                    onClick={() => {
                                                        setUserFilter(u);
                                                        setUserMenuOpen(false);
                                                        setTeamFilters([]); // Selection of user clears team filter
                                                    }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', cursor: 'pointer', background: isSel ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                                >
                                                    <span style={{ fontSize: '0.8rem', color: isSel ? 'var(--primary)' : '#374151', fontWeight: isSel ? 600 : 400 }}>{u}</span>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Channel Multi-Select */}
                    <div style={{ position: 'relative' }}>
                        <div 
                            className="input-fancy" 
                            style={{ height: '2.2rem', fontSize: '0.84rem', minWidth: '160px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'white' }}
                            onClick={() => setChannelMenuOpen(!channelMenuOpen)}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {channelFilters.length === 0 ? 'All Channels' : `${channelFilters.length} Channel${channelFilters.length > 1 ? 's' : ''}`}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>▼</span>
                        </div>

                        {channelMenuOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.35rem', background: 'white', border: '1px solid #E5E7EB', borderRadius: '0.65rem', width: '220px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                
                                <div style={{ display: 'flex', padding: '0.55rem', borderBottom: '1px solid #F3F4F6', gap: '0.35rem' }}>
                                    <input 
                                        placeholder="Search Channels..." 
                                        value={channelSearch} 
                                        onChange={e => setChannelSearch(e.target.value)} 
                                        style={{ flex: 1, padding: '0.35rem 0.65rem', fontSize: '0.75rem', borderRadius: '0.35rem', border: '1px solid #E5E7EB', outline: 'none' }} 
                                    />
                                    <button 
                                        style={{ background: '#F3F4F6', border: 'none', borderRadius: '0.35rem', padding: '0 0.5rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600, color: '#4B5563' }}
                                        onClick={() => setChannelSortAsc(!channelSortAsc)}
                                    >
                                        {channelSortAsc ? 'A-Z' : 'Z-A'}
                                    </button>
                                </div>

                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {(() => {
                                        const sorted = [...allChannels]
                                            .filter(c => c.toLowerCase().includes(channelSearch.toLowerCase()))
                                            .sort((a, b) => channelSortAsc ? a.localeCompare(b) : b.localeCompare(a));
                                        
                                        if (sorted.length === 0) return <div style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#9CA3AF', textAlign: 'center' }}>No channels found</div>;
                                        
                                        return sorted.map(c => {
                                            const isSel = channelFilters.includes(c);
                                            return (
                                                <div 
                                                    key={c}
                                                    onClick={() => {
                                                        setChannelFilters(prev => 
                                                            isSel ? prev.filter(x => x !== c) : [...prev, c]
                                                        );
                                                    }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', cursor: 'pointer', background: isSel ? 'rgba(160,43,147,0.05)' : 'transparent', borderBottom: '1px solid #F9FAFB' }}
                                                >
                                                    <input type="checkbox" checked={isSel} readOnly style={{ cursor: 'pointer' }} />
                                                    <span style={{ fontSize: '0.8rem', color: isSel ? 'var(--primary)' : '#374151', fontWeight: isSel ? 600 : 400 }}>{c}</span>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                <div style={{ padding: '0.55rem', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', background: '#F9FAFB' }}>
                                    <button 
                                        style={{ fontSize: '0.75rem', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                                        onClick={() => setChannelFilters([])}
                                    >
                                        Clear
                                    </button>
                                    <button 
                                        style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                                        onClick={() => setChannelMenuOpen(false)}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <input type="date" className="input-fancy" style={{ height: '2.2rem', fontSize: '0.84rem', width: '142px' }} value={startDate} onChange={e => setStartDate(e.target.value)} />
                    <span style={{ color: '#E5E7EB', fontSize: '0.8rem' }}>—</span>
                    <input type="date" className="input-fancy" style={{ height: '2.2rem', fontSize: '0.84rem', width: '142px' }} value={endDate} onChange={e => setEndDate(e.target.value)} />
                    {(teamFilters.length > 0 || userFilter !== 'All' || endDate !== getToday()) && (
                        <button className="button button-secondary" style={{ height: '2.2rem', fontSize: '0.8rem', padding: '0 0.85rem', color: 'var(--danger)' }}
                            onClick={() => { setTeamFilters([]); setUserFilter('All'); setStartDate(getLast30Days()); setEndDate(getToday()); }}>
                            Clear
                        </button>
                    )}

                </div>
            )}

            {/* ══════════ ANALYTICS ════════════════════════════ */}
            {activeTab === 'analytics' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                    


                    {/* KPI row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.85rem' }}>
                        <KpiCard label="Average Score"       value={kpi.avg !== '—' ? `${kpi.avg}%` : '—'} icon={Award}         color="#A02B93" />
                        <KpiCard label="Count of Users"      value={kpi.users.toLocaleString()}              icon={Users}         color="#14B8A6" />
                        <KpiCard label="Scored Sessions"     value={kpi.count.toLocaleString()}              icon={Activity}      color="#3B82F6" />
                        <KpiCard label="No Score / Partial"  value={kpi.noScore}                             icon={AlertCircle}   color="#F59E0B" />
                        <KpiCard label="Total Training Time" value={formatDuration(kpi.totalSecs)}      icon={Clock}         color="#10B981" />
                    </div>

                    {/* Score trend (full width) */}
                    <PanelCard icon={TrendingUp} title="Score Trend">
                        {trendData.length === 0 ? <EmptyChart /> : (
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={trendData} margin={{ top: 8, right: 12, left: -10, bottom: 24 }}>
                                    <defs>
                                        <linearGradient id="gScore" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="10%" stopColor="#A02B93" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#A02B93" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                                    <XAxis dataKey="name" stroke="#E5E7EB" fontSize={10} tickLine={false} axisLine={false} dy={6}>
                                        <Label value="Date" offset={-14} position="insideBottom" fill="#D1D5DB" fontSize={10} />
                                    </XAxis>
                                    <YAxis stroke="#E5E7EB" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} dx={-2}>
                                        <Label value="Score %" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: '#D1D5DB', fontSize: 10 }} offset={14} />
                                    </YAxis>
                                    <Tooltip content={<ChartTooltip />} />
                                    <Area connectNulls type="monotone" dataKey="score" name="Avg Score" unit="%"
                                        stroke="#A02B93" strokeWidth={2} fill="url(#gScore)"
                                        dot={{ r: 3.5, fill: '#A02B93', stroke: 'white', strokeWidth: 2 }}
                                        activeDot={{ r: 5, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </PanelCard>

                    {/* Channel + Category side-by-side */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>

                        {/* Channel */}
                        <PanelCard icon={BarChart2} title="Score by Channel" color="#3B82F6">
                            {channelData.length === 0 ? <EmptyChart h={190} /> : (
                                <ResponsiveContainer width="100%" height={190}>
                                    <BarChart data={channelData} margin={{ top: 16, right: 8, left: -10, bottom: 24 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                                        <XAxis dataKey="name" stroke="#E5E7EB" fontSize={10} tickLine={false} axisLine={false} dy={6}>
                                            <Label value="Channel" offset={-14} position="insideBottom" fill="#D1D5DB" fontSize={10} />
                                        </XAxis>
                                        <YAxis stroke="#E5E7EB" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} dx={-2} />
                                        <Tooltip content={<ChartTooltip />} />
                                        <Bar dataKey="score" name="Avg Score" unit="%" radius={[5, 5, 0, 0]} barSize={42} maxBarSize={55}>
                                            {channelData.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                            <LabelList dataKey="score" position="top" fill="#374151" fontSize={12} fontWeight={800} formatter={(v: number) => `${v}%`} offset={6} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </PanelCard>

                        {/* Radar */}
                        <PanelCard icon={Sparkles} title="Category Breakdown" color="#8B5CF6">
                            <ResponsiveContainer width="100%" height={260}>
                                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={categoryData}>
                                    <PolarGrid stroke="#F3F4F6" />
                                    <PolarAngleAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10, fontWeight: 600 }} />
                                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                                    <Radar name="Score" dataKey="score" stroke="#8B5CF6" strokeWidth={2} fill="#8B5CF6" fillOpacity={0.15} />
                                    <Tooltip content={<ChartTooltip />} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </PanelCard>
                    </div>

                    {/* Top / Bottom performers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                        {[
                            { title: 'Top Performers',        icon: Sparkles,    color: '#10B981', list: performers.top },
                            { title: 'Needs Attention',        icon: AlertCircle, color: '#EF4444', list: performers.bottom },
                        ].map(({ title, icon, color, list }) => (
                            <PanelCard key={title} title={title} icon={icon} color={color}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                                    {list.length === 0 && <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>No data.</p>}
                                    {list.map((u: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.5rem 0.75rem', borderRadius: '0.6rem', background: '#FAFAFA', border: '1px solid #F3F4F6' }}>
                                            <span style={{ width: '1.3rem', height: '1.3rem', borderRadius: '50%', background: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: '0.84rem', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                                            <span style={{ fontSize: '0.74rem', color: '#D1D5DB', flexShrink: 0 }}>{formatDuration(u.secs)}</span>
                                            <span className={`score-pill ${pillClass(u.avg)}`} style={{ fontSize: '0.78rem', padding: '0.15rem 0.6rem' }}>{u.avg}%</span>
                                        </div>
                                    ))}
                                </div>
                            </PanelCard>
                        ))}
                    </div>

                    {/* Growth Insights */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                        {[
                            { title: 'Top Strengths', icon: Sparkles, color: '#10B981', list: insights.strengths, isStrength: true },
                            { title: 'Priority Improvements', icon: TrendingDown, color: '#F59E0B', list: insights.improvements, isStrength: false },
                        ].map(({ title, icon, color, list, isStrength }) => (
                            <PanelCard key={title} title={title} icon={icon} color={color}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                                    {list.length === 0 && <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>No data available for insights.</p>}
                                    {list.map((c: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', gap: '0.85rem', padding: '0.85rem', borderRadius: '0.75rem', background: isStrength ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${isStrength ? '#DCFCE7' : '#FEF3C7'}` }}>
                                            <div style={{ background: color, color: 'white', width: '2rem', height: '2rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>
                                                {c.avg}%
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1F2937' }}>{c.label}</span>
                                                <p style={{ fontSize: '0.78rem', color: '#4B5563', lineHeight: 1.4, margin: 0 }}>
                                                    {CATEGORY_DESCRIPTIONS[c.key] || 'No description available for this category.'}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </PanelCard>
                        ))}
                    </div>


                    {/* Utilization */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                        {[
                            { title: 'Top 5 Teams by Utilization', icon: Activity, color: '#F59E0B', list: utilization.topTeams },
                            { title: 'Top 10 Agents by Utilization', icon: UserCog, color: '#3B82F6', list: utilization.topAgents },
                        ].map(({ title, icon, color, list }) => (
                            <PanelCard key={title} title={title} icon={icon} color={color}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                                    {list.length === 0 && <p style={{ color: '#D1D5DB', fontSize: '0.85rem' }}>No data.</p>}
                                    {list.map((u: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.5rem 0.75rem', borderRadius: '0.6rem', background: '#FAFAFA', border: '1px solid #F3F4F6' }}>
                                            <span style={{ width: '1.3rem', height: '1.3rem', borderRadius: '50%', background: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: '0.84rem', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                                            <span style={{ fontSize: '0.74rem', color: '#6B7280', flexShrink: 0, width: '50px', textAlign: 'right' }}>{u.sessions} sess</span>
                                            <span style={{ fontSize: '0.74rem', color: '#D1D5DB', flexShrink: 0, width: '55px', textAlign: 'right' }}>{formatDuration(u.secs)}</span>
                                            <span className={`score-pill ${u.avg ? pillClass(u.avg) : 'pill-low'}`} style={{ fontSize: '0.78rem', padding: '0.15rem 0.6rem', width: '45px', textAlign: 'center' }}>{u.avg ? `${u.avg}%` : '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            </PanelCard>
                        ))}
                    </div>

                </div>
            )}



            {showAssistant && <AdminAssistantModal onClose={() => setShowAssistant(false)} />}

            {editingAllocationUserId && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', width: '90%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Edit Minutes Allocation</h2>
                        <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1.5rem' }}>
                            Set the monthly session minutes allocated for this user. Enter a number or "unlimited".
                        </p>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>Allocated Minutes</label>
                            <input
                                type="text"
                                value={editAllocationValue}
                                onChange={(e) => setEditAllocationValue(e.target.value)}
                                style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #D1D5DB', fontSize: '1rem', outline: 'none' }}
                                placeholder="150 or unlimited"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setEditingAllocationUserId(null)}
                                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', background: '#F3F4F6', color: '#374151', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateAllocation}
                                disabled={actionLoading === 'updating_allocation'}
                                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', cursor: 'pointer', opacity: actionLoading === 'updating_allocation' ? 0.7 : 1 }}
                            >
                                {actionLoading === 'updating_allocation' ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
